import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { TYPOLOGY_KEYS, TYPOLOGIES } from '../compliance-safe/typologyData/typologies'
import { GamingApiFieldOptgroups, OrphanCombinedFieldOption } from './ruleBuilderFieldCatalog.jsx'
import RuleRiskLinksPanel from './components/RuleRiskLinksPanel'

const offenceOptions = [
  { code:'TF', desc:'Financing of terrorism' },
  { code:'ML', desc:'Money laundering' },
  { code:'OG', desc:'Offence against Commonwealth/State/Territory law' },
  { code:'FI', desc:'Person/agent not who they claim to be' },
  { code:'PC', desc:'Proceeds of crime' },
  { code:'TE', desc:'Tax evasion' }
]
const serviceOptions = [
  { code:'FIN_EFT', desc:'Electronic funds transfers (EFT)' },
  { code:'GAMCHSKL', desc:'Games of chance or skill' },
  { code:'GAM_BETT', desc:'Gambling and betting services' },
  { code:'GAM_MACH', desc:'Gaming machines' },
  { code:'VALCARDS', desc:'Stored value cards' }
]
const reasonOptions = [
  { code:'AV', desc:'Avoiding reporting obligations (structuring)' },
  { code:'UA', desc:'Unusual account activity' },
  { code:'UG', desc:'Unusual gambling activity' },
  { code:'UC', desc:'Unusually large cash transaction' },
  { code:'UT', desc:'Unusually large transfer' },
  { code:'IC', desc:'Inconsistent with customer profile' },
]

const RISK_ACTION_PRESETS = {
  low: {
    createAlert: true,
    alertSeverity: 'low',
    addToWatchlist: false,
    createCase: false,
    requireReview: false,
    notifyEmail: false,
    notifySMS: false,
    description: [
      'Create low-severity alert',
      'Log for compliance review',
      'No immediate escalation'
    ]
  },
  medium: {
    createAlert: true,
    alertSeverity: 'medium',
    addToWatchlist: true,
    createCase: false,
    requireReview: false,
    notifyEmail: true,
    notifySMS: false,
    description: [
      'Create medium-severity alert',
      'Add member to watchlist (30 days)',
      'Email notification to compliance team',
      'Enhanced monitoring activated'
    ]
  },
  high: {
    createAlert: true,
    alertSeverity: 'high',
    addToWatchlist: true,
    createCase: true,
    startECDD: false,
    startSMR: false,
    requireReview: false,
    notifyEmail: true,
    notifySMS: true,
    description: [
      'Create high-severity alert',
      'Add to watchlist (permanent)',
      'Open investigation case (High priority)',
      'Email + SMS notification to compliance manager',
      'Case assigned to AML team',
      'ECDD/SMR workflows available (optional)'
    ]
  },
  critical: {
    createAlert: true,
    alertSeverity: 'critical',
    addToWatchlist: true,
    createCase: true,
    startECDD: true,
    startSMR: false,
    requireReview: true,
    notifyEmail: true,
    notifySMS: true,
    description: [
      'Create critical alert',
      'Add to watchlist (permanent)',
      'Open urgent investigation case',
      'Auto-start ECDD workflow',
      'Transaction requires manager review',
      'Immediate notification (all channels) to Chief Compliance Officer',
      'SMR workflow available (trigger if investigation confirms suspicion)'
    ]
  }
}

function getDefaultActions(riskLevel) {
  return RISK_ACTION_PRESETS[riskLevel]?.description || []
}

export default function CombinedRuleBuilder() {
  const [searchParams] = useSearchParams()
  const ruleIdFromQuery = searchParams.get('id') || ''
  const focusFromQuery = searchParams.get('focus') || ''
  const [form, setForm] = React.useState({
    id:'', name:'', description:'',
    // S3 — mandatory rationale (≥ 20 chars) before a rule can be submitted.
    // rationale_source is read-only from the API; UI uses it to warn the
    // author when the rationale was only backfilled from description.
    rationale:'', rationale_source:null,
    type_ui:'Membership', // Membership | Gaming | Payout (mapped to backend types)
    category:'AML',
    severity:'MEDIUM',
    // S6-F1: optional per-tier severity overrides. Keyed by CDD tier
    // (simplified/standard/enhanced/prohibited). Empty = base severity only.
    severity_by_tier: {},
    enabled:true,
    processing_category:'session-based',
    time_window_days: '',
    aggregation:{},
    conditions:[{ field:'', operator:'equals', value:'', score: 20 }],
    actions:{ create_case:true, case_type:'SUSPICIOUS' },
    occurrence:'Ongoing', // Onboarding | Ongoing | Event
    typology:'', // SVCP typology for investigation case flows (structuring, velocity, etc.)
    // New fields
    priority: 50,
    tier: 'auto', // auto | 1 | 2
    subject: 'patron', // patron | egm | group (legacy: anonymous → egm + playerPopulation uncarded)
    playerPopulation: 'all', // all | carded | uncarded — who can trigger session rules
    geoScope: 'all', // all | zone | high_limit | vip
    zones: [],
    activeHours: '24_7', // 24_7 | night | business | custom
    hourFrom: '',
    hourTo: '',
    minKyc: 'any', // any | identified | verified
    patternType: 'threshold', // threshold | frequency | ratio | sequence | absence
    sessionReset: 'departure', // departure | inactivity | midnight
    inactivityMins: '15',
    scoreThreshold: 50,
    narrativeTemplate: ''
  })
  const [conditionMode, setConditionMode] = React.useState('simple') // simple | expression | pattern
  const [expressions, setExpressions] = React.useState([]) // array of expression strings
  const [tags, setTags] = React.useState({ offence_codes:[], service_codes:[], reason_codes:[] })
  const [riskLevel, setRiskLevel] = React.useState('medium') // low | medium | high | critical
  const [executionMode, setExecutionMode] = React.useState('recommend') // automatic | recommend | manual
  const [actions, setActions] = React.useState({
    createAlert: true,
    alertSeverity: 'medium',
    addToWatchlist: false,
    createCase: false,
    startECDD: false,
    startSMR: false,
    recalculatePatronRisk: false,
    requireReview: false,
    notifyEmail: false,
    notifySMS: false
  })
  // Pattern-specific state
  const [eventA, setEventA] = React.useState([{ field:'', operator:'equals', value:'', score: 20 }])
  const [eventB, setEventB] = React.useState([{ field:'', operator:'equals', value:'', score: 20 }])
  const [sequenceConfig, setSequenceConfig] = React.useState({ 
    withinMinutes: 30, 
    sameEgm: 'any' // any | same_egm | same_zone
  })
  const [frequencyConfig, setFrequencyConfig] = React.useState({ 
    n: 5, 
    withinMinutes: 15 
  })
  const [showActionCustomization, setShowActionCustomization] = React.useState(false)
  const [showAdvancedScope, setShowAdvancedScope] = React.useState(false)
  const [showScoringConfig, setShowScoringConfig] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [savedId, setSavedId] = React.useState('')
  const [testResult, setTestResult] = React.useState(null)
  const [testPayload, setTestPayload] = React.useState('{}')
  const [loadedId, setLoadedId] = React.useState('')
  const [loadedFromEngine, setLoadedFromEngine] = React.useState(false)

  // Alert Policy state
  const [alertPolicyEnabled, setAlertPolicyEnabled] = React.useState(false)
  const [alertPolicy, setAlertPolicy] = React.useState({
    mode: 'on_first_trigger',
    n: 3,
    suppress_hours: 168,
    dedupe_key_mode: 'member',
    escalation_override: { field: 'CashIn', aggregate_window_hours: 24, threshold: 5000 },
    and: []
  })
  const [policyValidation, setPolicyValidation] = React.useState({ valid: true, errors: [], warnings: [] })
  const [acknowledgeWarnings, setAcknowledgeWarnings] = React.useState(false)

  // Load rule for editing
  React.useEffect(() => {
    const load = async () => {
      const id = ruleIdFromQuery
      if (!id) return
      try {
        // Try engine first
        let rule = null
        try {
          const r = await authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(id)}`))
          if (r.ok) {
            const j = await r.json()
            if (j.success && j.data) { rule = j.data; setLoadedFromEngine(true) }
          }
        } catch {}
        // Fallback to DB record if engine miss
        if (!rule) {
          const r2 = await authFetch(getLocalApiUrl('/api/rules/db'))
          if (r2.ok) {
            const j2 = await r2.json()
            if (j2.success && Array.isArray(j2.data)) {
              const row = j2.data.find(r => (r.rule_id || r.id) === id)
              if (row) {
                // Convert DB row to engine-like rule object
                rule = {
                  id: row.rule_id || row.id,
                  name: row.name,
                  description: row.description || '',
                  rationale: row.rationale || '',
                  rationale_source: row.rationale_source || null,
                  type: row.type,
                  category: row.category,
                  severity: (row.severity || 'MEDIUM').toString().toUpperCase(),
                  enabled: row.enabled !== false,
                  processing_category: row.processing_category || 'session-based',
                  time_window_days: row.time_window_days || '',
                  aggregation: typeof row.aggregation === 'string' ? JSON.parse(row.aggregation) : (row.aggregation || {}),
                  conditions: typeof row.conditions === 'string' ? JSON.parse(row.conditions) : (row.conditions || []),
                  actions: typeof row.actions === 'string' ? JSON.parse(row.actions) : (row.actions || {}),
                  metadata: typeof row.metadata === 'string' ? JSON.parse(row.metadata) : (row.metadata || {})
                }
              }
            }
          }
        }
        if (!rule) return
        // Map backend type to UI type
        const uiType = rule.type === 'session' ? 'Gaming' : rule.type === 'onboarding' ? 'Membership' : 'Payout'
        // Normalize conditions to builder format
        let normalizedConditions = []
        let detectedExpressions = []
        let condPayload = rule.conditions
        if (typeof condPayload === 'string') {
          try { condPayload = JSON.parse(condPayload) } catch (_) { condPayload = null }
        }
        // Unwrap mistaken shape: [ { type, conditions: [...] } ]
        if (Array.isArray(condPayload) && condPayload.length === 1 && condPayload[0] && typeof condPayload[0] === 'object') {
          const inner = condPayload[0]
          if (inner.type && Array.isArray(inner.conditions) && inner.field == null && inner.expression == null) {
            condPayload = inner
          }
        }
        if (Array.isArray(condPayload)) {
          // Already a flat array (either field/operator/value objects or expression entries)
          const hasExpression = condPayload.some(c => !!c.expression)
          if (hasExpression) {
            detectedExpressions = condPayload.map(c => c.expression).filter(Boolean)
          } else {
            normalizedConditions = condPayload.map(c => ({ field:c.field||'', operator:c.operator||'equals', value:c.value??'' }))
          }
        } else if (condPayload && typeof condPayload === 'object') {
          if (condPayload.expression) {
            detectedExpressions = [condPayload.expression]
          } else if (condPayload.field) {
            // Single simple condition object
            normalizedConditions = [{
              field: condPayload.field || '',
              operator: condPayload.operator || 'equals',
              value: condPayload.value ?? ''
            }]
          } else if (Array.isArray(condPayload.conditions)) {
            // conditions: { type:'and', conditions:[ {field,operator,value} | {expression} ] }
            const arr = condPayload.conditions
            const hasExpression = arr.some(c => !!c.expression)
            if (hasExpression) {
              detectedExpressions = arr.map(c => c.expression).filter(Boolean)
            } else {
              normalizedConditions = arr.map(c => ({ field:c.field||'', operator:c.operator||'equals', value:c.value??'' }))
            }
          }
        }

        const meta = rule.metadata || {}
        const base = {
          id: rule.id,
          name: rule.name || '',
          description: rule.description || '',
          rationale: rule.rationale || '',
          rationale_source: rule.rationale_source || null,
          type_ui: uiType,
          category: rule.category || 'AML',
          severity: (rule.severity || 'MEDIUM').toString().toUpperCase(),
          severity_by_tier: (rule.severity_by_tier && typeof rule.severity_by_tier === 'object') ? rule.severity_by_tier : {},
          enabled: rule.enabled !== false,
          processing_category: rule.processing_category || 'session-based',
          time_window_days: rule.time_window_days || '',
          aggregation: rule.aggregation || {},
          conditions: normalizedConditions.length ? normalizedConditions : [{ field:'', operator:'equals', value:'' }],
          actions: rule.actions || { create_case:true, case_type:'SUSPICIOUS' },
          occurrence: meta.occurrence || 'Ongoing',
          typology: meta.typology || '',
          // Load new metadata fields
          priority: meta.priority ?? 50,
          tier: meta.tier || 'auto',
          subject: meta.subject === 'anonymous' ? 'egm' : (meta.subject || 'patron'),
          playerPopulation: meta.playerPopulation
            || (meta.subject === 'anonymous' ? 'uncarded' : 'all'),
          geoScope: meta.geoScope || 'all',
          zones: meta.zones || [],
          activeHours: meta.activeHours || '24_7',
          hourFrom: meta.hourFrom || '',
          hourTo: meta.hourTo || '',
          minKyc: meta.minKyc || 'any',
          patternType: meta.patternType || 'threshold',
          sessionReset: meta.sessionReset || 'departure',
          inactivityMins: meta.inactivityMins || '15',
          scoreThreshold: meta.scoreThreshold ?? 50,
          narrativeTemplate: meta.narrativeTemplate || ''
        }
        if (detectedExpressions && detectedExpressions.length) {
          setConditionMode('expression')
          setExpressions(detectedExpressions)
          base.conditions = [{ field:'', operator:'equals', value:'' }]
        } else {
          setConditionMode('simple')
          setExpressions([])
        }
        setForm(base)
        
        // Load pattern-specific configurations
        const patternType = meta.patternType || 'threshold'
        if (patternType === 'sequence' || patternType === 'absence') {
          // Load eventA, eventB, sequenceConfig from condPayload
          if (condPayload && condPayload.type === patternType) {
            if (Array.isArray(condPayload.eventA) && condPayload.eventA.length) {
              setEventA(condPayload.eventA.map(c => ({ ...c, score: c.score ?? 20 })))
            }
            if (Array.isArray(condPayload.eventB) && condPayload.eventB.length) {
              setEventB(condPayload.eventB.map(c => ({ ...c, score: c.score ?? 20 })))
            }
            if (condPayload.sequenceConfig) {
              setSequenceConfig({
                withinMinutes: condPayload.sequenceConfig.withinMinutes ?? 30,
                sameEgm: condPayload.sequenceConfig.sameEgm || 'any'
              })
            }
            setConditionMode('pattern')
          }
        } else if (patternType === 'frequency') {
          // Load frequency config
          if (condPayload && condPayload.type === 'frequency' && condPayload.config) {
            setFrequencyConfig({
              n: condPayload.config.n ?? 5,
              withinMinutes: condPayload.config.withinMinutes ?? 15
            })
          }
          // Conditions are already loaded in normalizedConditions
        } else if (patternType === 'ratio') {
          // For ratio, conditions are stored differently - reconstruct into form.conditions[0]
          if (condPayload && condPayload.type === 'ratio') {
            base.conditions = [{
              field: condPayload.fieldA || '',
              operator: condPayload.operator || 'greater_than',
              value: condPayload.value || '',
              score: condPayload.score ?? 20
            }]
            if (condPayload.fieldB) {
              base.aggregation = { ...base.aggregation, ratioFieldB: condPayload.fieldB }
            }
            setForm(base) // Update form again with ratio conditions
          }
        }
        
        setLoadedId(rule.id)
        if (!loadedFromEngine) setLoadedFromEngine(false)
        // Load tags
        try {
          const tr = await authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(id)}/tags`))
          const tj = await tr.json()
          if (tj.success && tj.data) setTags({
            offence_codes: tj.data.offence_codes || [],
            service_codes: tj.data.service_codes || [],
            reason_codes: tj.data.reason_codes || []
          })
        } catch {}
      } catch {}
    }
    load()
  }, [ruleIdFromQuery])

  React.useEffect(() => {
    if (focusFromQuery !== 'rationale' || !ruleIdFromQuery) return
    if (!loadedId && !form.id) return
    const t = window.setTimeout(() => {
      const el = document.getElementById('rule-rationale-field')
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const ta = el.querySelector('textarea')
      if (ta) ta.focus()
    }, 350)
    return () => window.clearTimeout(t)
  }, [focusFromQuery, ruleIdFromQuery, loadedId, form.id])

  const updateCondition = (idx, key, val) => {
    setForm(prev => ({ ...prev, conditions: prev.conditions.map((c,i)=> i===idx ? ({ ...c, [key]: val }) : c) }))
  }

  // S6: per-tier threshold overrides. Writes `value_by_tier[tier]` on the
  // condition. Setting an empty value clears that tier; clearing all tiers
  // drops the `value_by_tier` key entirely so existing rules serialize clean.
  const updateConditionTier = (idx, tier, val) => {
    setForm(prev => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => {
        if (i !== idx) return c
        const byTier = { ...(c.value_by_tier || {}) }
        const isEmpty = val === '' || val === null || val === undefined
        if (isEmpty) delete byTier[tier]
        else byTier[tier] = Number(val)
        const next = { ...c, value_by_tier: byTier }
        if (Object.keys(byTier).length === 0) delete next.value_by_tier
        return next
      }),
    }))
  }
  const [tierOpen, setTierOpen] = React.useState({})
  // S6-F1: severity panel expansion (rule-level, not per-condition).
  const [severityTierOpen, setSeverityTierOpen] = React.useState(false)

  const addCondition = () => setForm(prev => ({ ...prev, conditions:[...prev.conditions, { field:'', operator:'equals', value:'', score: 20 }] }))
  const removeCondition = (idx) => setForm(prev => ({ ...prev, conditions: prev.conditions.filter((_,i)=>i!==idx) }))

  const updateEventA = (idx, key, val) => {
    setEventA(prev => prev.map((c,i)=> i===idx ? ({ ...c, [key]: val }) : c))
  }
  const addEventA = () => setEventA(prev => [...prev, { field:'', operator:'equals', value:'', score: 20 }])
  const removeEventA = (idx) => setEventA(prev => prev.filter((_,i)=>i!==idx))

  const updateEventB = (idx, key, val) => {
    setEventB(prev => prev.map((c,i)=> i===idx ? ({ ...c, [key]: val }) : c))
  }
  const addEventB = () => setEventB(prev => [...prev, { field:'', operator:'equals', value:'', score: 20 }])
  const removeEventB = (idx) => setEventB(prev => prev.filter((_,i)=>i!==idx))

  // Calculate total score for weighted scoring
  const calculateTotalScore = () => {
    if (form.patternType === 'sequence' || form.patternType === 'absence') {
      const scoreA = eventA.filter(c => c.field).reduce((sum, c) => sum + (c.score || 0), 0)
      const scoreB = eventB.filter(c => c.field).reduce((sum, c) => sum + (c.score || 0), 0)
      return scoreA + scoreB
    }
    return form.conditions.filter(c => c.field).reduce((sum, c) => sum + (c.score || 0), 0)
  }

  const handleRiskLevelChange = (newLevel) => {
    setRiskLevel(newLevel)
    // Auto-set execution mode based on risk level
    // Critical: automatic, High/Medium: recommend, Low: manual
    if (newLevel === 'critical') {
      setExecutionMode('automatic')
    } else if (newLevel === 'high' || newLevel === 'medium') {
      setExecutionMode('recommend')
    } else if (newLevel === 'low') {
      setExecutionMode('manual')
    }
    // Auto-populate actions with preset defaults
    const preset = RISK_ACTION_PRESETS[newLevel]
    if (preset) {
      setActions({
        createAlert: preset.createAlert,
        alertSeverity: preset.alertSeverity,
        addToWatchlist: preset.addToWatchlist,
        createCase: preset.createCase,
        startECDD: preset.startECDD,
        startSMR: preset.startSMR,
        requireReview: preset.requireReview,
        notifyEmail: preset.notifyEmail,
        notifySMS: preset.notifySMS
      })
    }
  }

  // Validate alert policy on change
  const validateAlertPolicy = React.useCallback(async (policy) => {
    try {
      const res = await authFetch(getLocalApiUrl('/api/alerts/validate-policy'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alert_policy: policy, rule: { severity: form.severity } })
      })
      const json = await res.json()
      if (json.success) {
        setPolicyValidation({ valid: json.valid, errors: json.errors || [], warnings: json.warnings || [] })
      }
    } catch (_) {
      // Validation API may not be available — skip
    }
  }, [form.severity])

  React.useEffect(() => {
    if (alertPolicyEnabled && alertPolicy.mode) {
      const timer = setTimeout(() => validateAlertPolicy(alertPolicy), 300)
      return () => clearTimeout(timer)
    } else {
      setPolicyValidation({ valid: true, errors: [], warnings: [] })
    }
  }, [alertPolicyEnabled, alertPolicy, validateAlertPolicy])

  const saveRule = async () => {
    setSaving(true)
    try {
      const payload = { ...form }
      // rationale_source is read-only on the API (server stamps 'author' when
      // the author saves a non-empty rationale, or keeps existing 'backfill').
      // Don't echo it back so the backend can't be tricked into marking a
      // backfilled rationale as reviewed.
      delete payload.rationale_source
      // S6-F1: drop severity_by_tier when empty so rules without overrides
      // serialize identically to pre-S6-F1 rules.
      if (
        payload.severity_by_tier &&
        typeof payload.severity_by_tier === 'object' &&
        Object.keys(payload.severity_by_tier).length === 0
      ) {
        delete payload.severity_by_tier
      }
      // Map UI type to backend type
      const typeMap = { Membership: 'onboarding', Gaming: 'session', Payout: 'transaction' }
      payload.type = typeMap[form.type_ui] || 'transaction'
      delete payload.type_ui
      // store occurrence, typology, and new fields in metadata
      payload.metadata = { 
        ...(payload.metadata||{}), 
        occurrence: form.occurrence,
        priority: form.priority,
        tier: form.tier,
        subject: form.subject,
        playerPopulation: form.playerPopulation || 'all',
        geoScope: form.geoScope,
        zones: form.zones,
        activeHours: form.activeHours,
        hourFrom: form.hourFrom,
        hourTo: form.hourTo,
        minKyc: form.minKyc,
        patternType: form.patternType,
        sessionReset: form.sessionReset,
        inactivityMins: form.inactivityMins,
        scoreThreshold: form.scoreThreshold,
        narrativeTemplate: form.narrativeTemplate
      }
      if (form.typology) payload.metadata.typology = form.typology

      // Handle pattern-specific conditions
      if (conditionMode === 'expression') {
        const cleaned = (expressions || []).map(e => (e || '').trim()).filter(Boolean)
        // Engine expects { type:'and', conditions:[{expression},…] } (not a bare array) for multi-clause AND.
        payload.conditions = cleaned.length
          ? { type: 'and', conditions: cleaned.map((e) => ({ expression: e })) }
          : []
      } else if (form.patternType === 'sequence' || form.patternType === 'absence') {
        // Store as pattern with event groups
        payload.conditions = {
          type: form.patternType,
          eventA: eventA.filter(c => c.field),
          eventB: eventB.filter(c => c.field),
          sequenceConfig
        }
      } else if (form.patternType === 'frequency') {
        payload.conditions = {
          type: 'frequency',
          config: frequencyConfig,
          conditions: form.conditions.filter(c => c.field)
        }
      } else if (form.patternType === 'ratio') {
        // Store ratio pattern with both fields
        payload.conditions = {
          type: 'ratio',
          fieldA: form.conditions[0]?.field || '',
          fieldB: form.aggregation?.ratioFieldB || '',
          operator: form.conditions[0]?.operator || 'greater_than',
          value: form.conditions[0]?.value || '',
          score: form.conditions[0]?.score || 20
        }
      } else {
        // Standard threshold
        payload.conditions = form.conditions.filter(c => c.field)
      }

      // normalize numeric
      if (payload.time_window_days === '') delete payload.time_window_days

      // Attach alert_policy to actions if enabled
      if (alertPolicyEnabled && alertPolicy.mode) {
        if (!payload.actions) payload.actions = {}
        const policyToSave = { mode: alertPolicy.mode }
        if (alertPolicy.mode === 'on_nth_trigger') policyToSave.n = Number(alertPolicy.n) || 3
        if (alertPolicy.mode === 'on_first_then_suppress') {
          policyToSave.suppress_hours = Number(alertPolicy.suppress_hours) || 168
          if (alertPolicy.escalation_override?.field && alertPolicy.escalation_override?.threshold) {
            policyToSave.escalation_override = {
              field: alertPolicy.escalation_override.field,
              aggregate_window_hours: Number(alertPolicy.escalation_override.aggregate_window_hours) || 24,
              threshold: Number(alertPolicy.escalation_override.threshold)
            }
          }
        }
        if (alertPolicy.dedupe_key_mode) policyToSave.dedupe_key_mode = alertPolicy.dedupe_key_mode
        if (alertPolicy.and && alertPolicy.and.length > 0) policyToSave.and = alertPolicy.and
        payload.actions.alert_policy = policyToSave
        if (acknowledgeWarnings) payload.acknowledge_warnings = true
      }
      // Decide create vs update based on whether we loaded an existing rule
      const isUpdate = !!(loadedId || savedId)
      const method = isUpdate ? 'PUT' : 'POST'
      const idForSave = isUpdate ? (loadedId || savedId) : payload.id
      const url = method === 'POST' ? getLocalApiUrl('/api/rules') : getLocalApiUrl(`/api/rules/${encodeURIComponent(idForSave)}`)
      let r = await authFetch(url, { method, headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload) })
      let j = await r.json()
      // Fallback: if PUT failed with 404, try POST (e.g., rule not present in engine yet)
      if (!j.success && method==='PUT' && r.status===404) {
        r = await authFetch(getLocalApiUrl('/api/rules'), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload) })
        j = await r.json()
      }
      if (j.success) {
        setSavedId(payload.id)
        setLoadedFromEngine(true)
        // Track the active rule id for subsequent updates
        setLoadedId(idForSave || payload.id)
        // save tags if any
        if (tags.offence_codes.length || tags.service_codes.length || tags.reason_codes.length) {
          await authFetch(getLocalApiUrl(`/api/rules/${payload.id}/tags`), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(tags) })
        }
        // save risk level, execution mode, and actions
        await authFetch(getLocalApiUrl(`/api/rules/${payload.id}/actions`), {
          method:'POST',
          headers:{'Content-Type':'application/json'},
          body: JSON.stringify({ risk_level: riskLevel, execution_mode: executionMode, ...actions })
        })
        alert('Rule saved')
      } else {
        alert(j.error || 'Failed to save rule')
      }
    } catch (e) {
      alert('Error saving rule: ' + e.message)
    } finally { setSaving(false) }
  }

  const runTest = async () => {
    try {
      const id = savedId || form.id
      if (!id) return alert('Save the rule first')
      const body = { testType:'custom', data: JSON.parse(testPayload||'{}') }
      const r = await authFetch(getLocalApiUrl(`/api/rules/${id}/test`), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(body) })
      const j = await r.json(); setTestResult(j)
    } catch (e) { alert('Test error: '+e.message) }
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 space-y-6">
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Unified Rule Builder</h2>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Rule ID</label>
            <input className="input-field" value={form.id} onChange={e=>setForm(prev=>({...prev, id:e.target.value}))} disabled={!!loadedId} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Rule Name</label>
            <input className="input-field" value={form.name} onChange={e=>setForm(prev=>({...prev, name:e.target.value}))} />
          </div>
          <div id="rule-rationale-field">
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs text-gray-600">
                Rationale <span className="text-red-500">*</span>
                <span className="text-gray-400 font-normal ml-1">(why this rule exists, what risk it addresses, evidence behind thresholds)</span>
              </label>
              <span className={`text-xs font-mono ${form.rationale.trim().length >= 20 ? 'text-green-600' : 'text-gray-400'}`}>
                {form.rationale.trim().length} / 20 min
              </span>
            </div>
            <textarea
              className="input-field"
              rows={4}
              value={form.rationale}
              onChange={e=>setForm(prev=>({...prev, rationale:e.target.value, rationale_source:'author'}))}
              placeholder={'e.g. "Addresses structuring risk RA-2026-037. Threshold $9,500 set just below the $10k TTR cutoff; evidence: 3 of last 6 quarters showed members depositing multi-venue amounts totalling $9.5k+ over 24h."'}
            />
            {form.rationale_source === 'backfill' && form.rationale.trim().length > 0 && (
              <div className="mt-1 p-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800">
                <b>Backfill rationale — needs review.</b> This text was copied from the rule's description by the S3 migration and has not yet been reviewed by a human. Rewrite or confirm the rationale, then save — it will stamp as author-reviewed and become submittable.
              </div>
            )}
            {form.rationale.trim().length > 0 && form.rationale.trim().length < 20 && (
              <p className="mt-1 text-xs text-red-600">
                Minimum 20 characters required before this rule can be submitted.
              </p>
            )}
          </div>
          <RuleRiskLinksPanel ruleId={loadedId || savedId || ''} />
          <div>
            <label className="block text-xs text-gray-600 mb-1">Type</label>
            <select className="select-field" value={form.type_ui} onChange={e=>setForm(prev=>({...prev, type_ui:e.target.value}))}>
              <option>Membership</option>
              <option>Gaming</option>
              <option>Payout</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Processing Category</label>
            <select className="select-field" value={form.processing_category} onChange={e=>setForm(prev=>({...prev, processing_category:e.target.value}))}>
              <option value="session-based">Session-based</option>
              <option value="cumulative">Cumulative</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Occurrence</label>
            <select className="select-field" value={form.occurrence} onChange={e=>setForm(prev=>({...prev, occurrence:e.target.value}))}>
              <option>Onboarding</option>
              <option>Ongoing</option>
              <option>Event</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Investigation Typology</label>
            <select className="select-field" value={form.typology || ''} onChange={e=>setForm(prev=>({...prev, typology:e.target.value}))}>
              <option value="">— Not set (auto-detect from rule name) —</option>
              {TYPOLOGY_KEYS.map(k => (
                <option key={k} value={k}>{TYPOLOGIES[k]?.label || k}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-0.5">Links rule to SVCP decision tree when creating investigation cases</p>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Severity</label>
            <select className="select-field" value={form.severity} onChange={e=>setForm(prev=>({...prev, severity:e.target.value}))}>
              <option value="LOW">LOW</option>
              <option value="MEDIUM">MEDIUM</option>
              <option value="HIGH">HIGH</option>
              <option value="CRITICAL">CRITICAL</option>
            </select>
            {(() => {
              const byTier = form.severity_by_tier || {}
              const hasOverrides = Object.keys(byTier).length > 0
              const panelOpen = severityTierOpen || hasOverrides
              return (
                <div className="mt-1">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="text-[11px] font-semibold text-slate-600 hover:text-slate-900"
                      onClick={() => setSeverityTierOpen(v => !panelOpen)}
                    >
                      {panelOpen ? '− Hide per-tier severity' : '+ Per-tier severity (S6-F1)'}
                    </button>
                    {hasOverrides && !severityTierOpen && (
                      <span className="text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                        Tier severity active
                      </span>
                    )}
                  </div>
                  {panelOpen && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                      {['simplified','standard','enhanced','prohibited'].map(tier => (
                        <div key={tier}>
                          <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-0.5">{tier}</label>
                          <select
                            className="select-field text-xs py-1"
                            value={byTier[tier] || ''}
                            onChange={(e) => {
                              const next = { ...(form.severity_by_tier || {}) }
                              if (!e.target.value) delete next[tier]
                              else next[tier] = e.target.value
                              setForm(prev => ({ ...prev, severity_by_tier: next }))
                            }}
                          >
                            <option value="">(base)</option>
                            <option value="LOW">LOW</option>
                            <option value="MEDIUM">MEDIUM</option>
                            <option value="HIGH">HIGH</option>
                            <option value="CRITICAL">CRITICAL</option>
                          </select>
                        </div>
                      ))}
                      <p className="col-span-2 md:col-span-4 text-[10px] text-slate-500 italic">
                        When a patron's CDD tier (S2) matches, the tier severity replaces the base severity on the published alert. Leave a tier as (base) to fall back.
                      </p>
                    </div>
                  )}
                </div>
              )
            })()}
          </div>
          {form.processing_category==='cumulative' && (
            <div>
              <label className="block text-xs text-gray-600 mb-1">Cumulative Window (days)</label>
              <input type="number" min="0" className="input-field" value={form.time_window_days} onChange={e=>setForm(prev=>({...prev, time_window_days:e.target.value}))} />
            </div>
          )}
        </div>
      </div>

      {/* Priority and Processing Tier */}
      <div className="card">
        <h3 className="font-semibold mb-3">Priority & Processing</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Rule Priority</label>
            <div className="flex items-center gap-3">
              <input 
                type="range" 
                min="1" 
                max="100" 
                value={form.priority}
                onChange={e => setForm(prev => ({...prev, priority: parseInt(e.target.value)}))}
                className="flex-1"
              />
              <span className="text-sm font-semibold text-blue-600 min-w-[3rem] text-center">{form.priority}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Optional catalog value (1 = highest). Rules run independently; each match can raise its own alert unless you add consolidation later.
            </p>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Processing Tier</label>
            <select 
              className="select-field" 
              value={form.tier} 
              onChange={e => setForm(prev => ({...prev, tier: e.target.value}))}
            >
              <option value="auto">Auto-detect from window type</option>
              <option value="1">Tier 1 — Point-in-Time (immediate)</option>
              <option value="2">Tier 2 — Cumulative (scheduled)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {form.tier === 'auto' && 'Session-based → Tier 1, Cumulative → Tier 2'}
              {form.tier === '1' && 'Fires immediately on transaction/payout ingest'}
              {form.tier === '2' && 'Runs on schedule (2-3 hrs + EOD sweep)'}
            </p>
          </div>
        </div>
      </div>

      {/* Advanced Scope Configuration */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Scope & Context</h3>
          <button 
            type="button"
            className="text-xs text-blue-600 hover:underline"
            onClick={() => setShowAdvancedScope(!showAdvancedScope)}
          >
            {showAdvancedScope ? 'Hide Advanced Scope' : 'Configure Scope'}
          </button>
        </div>
        
        {showAdvancedScope && (
          <div className="space-y-4 border-t pt-3">
            <div>
              <label className="block text-xs text-gray-600 mb-2">Primary Subject</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'patron', label: 'Patron/Member', desc: 'Track by patron ID' },
                  { id: 'egm', label: 'EGM Machine', desc: 'Monitor specific EGM' },
                  { id: 'group', label: 'Group/Cluster', desc: 'Multiple patrons' }
                ].map(s => (
                  <label 
                    key={s.id}
                    className={`
                      p-3 border-2 rounded-lg cursor-pointer transition text-center
                      ${form.subject === s.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}
                    `}
                  >
                    <input 
                      type="radio" 
                      name="subject" 
                      value={s.id}
                      checked={form.subject === s.id}
                      onChange={e => setForm(prev => ({...prev, subject: e.target.value}))}
                      className="sr-only"
                    />
                    <div className="font-medium text-sm">{s.label}</div>
                    <div className="text-xs text-gray-500 mt-1">{s.desc}</div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-2">Player population</label>
              <p className="text-[11px] text-gray-500 mb-2">
                Who can trigger this rule. Session rules (e.g. bill stuffing) usually use All.
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'all', label: 'All sessions', desc: 'Carded + uncarded' },
                  { id: 'carded', label: 'Carded only', desc: 'Loyalty card swiped' },
                  { id: 'uncarded', label: 'Uncarded only', desc: 'Anonymous / virtual sits' },
                ].map((p) => (
                  <label
                    key={p.id}
                    className={`
                      p-3 border-2 rounded-lg cursor-pointer transition text-center
                      ${form.playerPopulation === p.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}
                    `}
                  >
                    <input
                      type="radio"
                      name="playerPopulation"
                      value={p.id}
                      checked={form.playerPopulation === p.id}
                      onChange={(e) => setForm((prev) => ({ ...prev, playerPopulation: e.target.value }))}
                      className="sr-only"
                    />
                    <div className="font-medium text-sm">{p.label}</div>
                    <div className="text-xs text-gray-500 mt-1">{p.desc}</div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Geographic Scope</label>
              <select 
                className="select-field" 
                value={form.geoScope} 
                onChange={e => setForm(prev => ({...prev, geoScope: e.target.value}))}
              >
                <option value="all">All Areas — Entire venue</option>
                <option value="zone">Specific Zone(s)</option>
                <option value="high_limit">High Limit Area Only</option>
                <option value="vip">VIP Room Only</option>
              </select>
              {form.geoScope === 'zone' && (
                <div className="mt-2 flex gap-2 flex-wrap">
                  {['Zone A', 'Zone B', 'Zone C', 'Zone D'].map(zone => {
                    const selected = (form.zones || []).includes(zone)
                    return (
                      <button
                        key={zone}
                        type="button"
                        onClick={() => {
                          setForm(prev => ({
                            ...prev,
                            zones: selected 
                              ? (prev.zones || []).filter(z => z !== zone)
                              : [...(prev.zones || []), zone]
                          }))
                        }}
                        className={`
                          px-3 py-1 rounded-full text-xs font-medium transition
                          ${selected 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
                        `}
                      >
                        {zone}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Active Hours</label>
              <select 
                className="select-field" 
                value={form.activeHours} 
                onChange={e => setForm(prev => ({...prev, activeHours: e.target.value}))}
              >
                <option value="24_7">24/7 — Always active</option>
                <option value="night">Night only (10 pm–6 am)</option>
                <option value="business">Business hours (9 am–5 pm)</option>
                <option value="custom">Custom window</option>
              </select>
              {form.activeHours === 'custom' && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <input 
                    type="number" 
                    min="0" 
                    max="23"
                    className="input-field" 
                    placeholder="From hour (0-23)"
                    value={form.hourFrom}
                    onChange={e => setForm(prev => ({...prev, hourFrom: e.target.value}))}
                  />
                  <input 
                    type="number" 
                    min="0" 
                    max="23"
                    className="input-field" 
                    placeholder="To hour (0-23)"
                    value={form.hourTo}
                    onChange={e => setForm(prev => ({...prev, hourTo: e.target.value}))}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Minimum Data Confidence</label>
              <select 
                className="select-field" 
                value={form.minKyc} 
                onChange={e => setForm(prev => ({...prev, minKyc: e.target.value}))}
              >
                <option value="any">Any — Include anonymous activity</option>
                <option value="identified">Identified only (member card swiped)</option>
                <option value="verified">Verified KYC only</option>
              </select>
            </div>

            {form.processing_category === 'session-based' && (
              <div>
                <label className="block text-xs text-gray-600 mb-1">Session Reset Trigger</label>
                <select 
                  className="select-field" 
                  value={form.sessionReset} 
                  onChange={e => setForm(prev => ({...prev, sessionReset: e.target.value}))}
                >
                  <option value="departure">Patron departs (card out / EGM idle)</option>
                  <option value="inactivity">Inactivity period</option>
                  <option value="midnight">Midnight (calendar day rollover)</option>
                </select>
                {form.sessionReset === 'inactivity' && (
                  <input 
                    type="number"
                    min="1"
                    className="input-field mt-2" 
                    placeholder="Inactivity threshold (minutes)"
                    value={form.inactivityMins}
                    onChange={e => setForm(prev => ({...prev, inactivityMins: e.target.value}))}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card">
        <h3 className="font-semibold mb-2">Pattern Type & Conditions</h3>
        
        {/* Pattern Type Selector */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <label className="block text-xs text-gray-600 mb-2">Detection Pattern</label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[
              { value: 'threshold', label: 'Threshold', icon: '⊙', desc: 'Single event breach' },
              { value: 'frequency', label: 'Frequency', icon: '⟳', desc: 'N times in Y mins' },
              { value: 'ratio', label: 'Ratio', icon: '÷', desc: 'Compare A ÷ B' },
              { value: 'sequence', label: 'Sequence', icon: '→', desc: 'A then B' },
              { value: 'absence', label: 'Absence', icon: '↛', desc: 'A then NOT B' }
            ].map(p => {
              const selected = form.patternType === p.value
              return (
                <label
                  key={p.value}
                  className={`
                    p-2 border-2 rounded-lg cursor-pointer transition text-center
                    ${selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}
                  `}
                >
                  <input
                    type="radio"
                    name="patternType"
                    value={p.value}
                    checked={selected}
                    onChange={e => setForm(prev => ({...prev, patternType: e.target.value}))}
                    className="sr-only"
                  />
                  <div className="text-xl mb-1">{p.icon}</div>
                  <div className="text-xs font-semibold">{p.label}</div>
                  <div className="text-xs text-gray-500">{p.desc}</div>
                </label>
              )
            })}
          </div>
          
          {/* Frequency Config */}
          {form.patternType === 'frequency' && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
              <div className="text-xs font-semibold text-blue-800 mb-2">Frequency Configuration</div>
              <div className="flex items-center gap-2 text-sm flex-wrap mb-3">
                <span className="text-gray-700">Trigger when at least</span>
                <input
                  type="number"
                  min="2"
                  className="input-field w-16 py-1"
                  value={frequencyConfig.n}
                  onChange={e => setFrequencyConfig(prev => ({...prev, n: parseInt(e.target.value) || 2}))}
                />
                <span className="text-gray-700">matching events occur within</span>
                <input
                  type="number"
                  min="1"
                  className="input-field w-16 py-1"
                  value={frequencyConfig.withinMinutes}
                  onChange={e => setFrequencyConfig(prev => ({...prev, withinMinutes: parseInt(e.target.value) || 15}))}
                />
                <span className="text-gray-700">minutes</span>
              </div>
              
              <div className="p-2 bg-white border border-blue-300 rounded text-xs">
                <strong className="text-blue-800">How it works:</strong>
                <div className="text-gray-700 mt-1">
                  The system will look back {frequencyConfig.withinMinutes} minutes and count how many events match 
                  the conditions below. If the count is ≥ {frequencyConfig.n}, the rule fires.
                </div>
                <div className="mt-2 text-blue-700 font-medium">
                  Example: "{frequencyConfig.n || 5} transactions of Cash In &gt; $10,000 within {frequencyConfig.withinMinutes || 15} minutes"
                </div>
              </div>
              
              <div className="mt-3 text-xs text-gray-600">
                <strong>💡 Tip:</strong> Define conditions below to specify WHAT events to count. 
                Each matching event in the time window adds to the count.
              </div>
            </div>
          )}
          
          {/* Ratio Config */}
          {form.patternType === 'ratio' && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
              <div className="text-xs font-semibold text-green-800 mb-2">Configure Ratio Comparison</div>
              <div className="grid grid-cols-1 md:grid-cols-7 gap-2 items-center text-sm">
                <div className="md:col-span-2">
                  <label className="text-xs text-gray-600 block mb-1">Field A (Numerator)</label>
                  <select 
                    className="select-field text-sm py-1"
                    value={form.conditions[0]?.field || ''}
                    onChange={e => {
                      const newConds = [...form.conditions]
                      if (!newConds[0]) newConds[0] = { field: '', operator: 'ratio', value: '', score: 20 }
                      newConds[0].field = e.target.value
                      setForm(prev => ({...prev, conditions: newConds}))
                    }}
                  >
                    <option value="">Select Field A</option>
                    <OrphanCombinedFieldOption field={form.conditions[0]?.field} />
                    <optgroup label="Financial">
                      <option value="CashIn">Cash In Amount</option>
                      <option value="CashOut">Cash Out Amount</option>
                      <option value="Turnover">Turnover / Wager</option>
                      <option value="NetAmount">Net Amount</option>
                    </optgroup>
                    <optgroup label="Gaming">
                      <option value="sessions.enriched.cash_in_sum">Session Cash In</option>
                      <option value="sessions.enriched.turnover_currency">Session Turnover</option>
                      <option value="TotalWon">Total Won</option>
                      <option value="TotalWagered">Total Wagered</option>
                    </optgroup>
                    <optgroup label="Gaming • Meter / combined (API keys)">
                      <option value="Turnover">Turnover</option>
                      <option value="CashIn">CashIn</option>
                      <option value="CashDrop">CashDrop</option>
                      <option value="Bill50">Bill50</option>
                      <option value="Bill100">Bill100</option>
                      <option value="Turnover_alloc">Turnover_alloc</option>
                      <option value="Bill100_alloc">Bill100_alloc</option>
                    </optgroup>
                  </select>
                </div>
                
                <div className="text-center text-2xl text-gray-600 font-bold">÷</div>
                
                <div className="md:col-span-2">
                  <label className="text-xs text-gray-600 block mb-1">Field B (Denominator)</label>
                  <select 
                    className="select-field text-sm py-1"
                    value={form.aggregation?.ratioFieldB || ''}
                    onChange={e => setForm(prev => ({
                      ...prev, 
                      aggregation: {...prev.aggregation, ratioFieldB: e.target.value}
                    }))}
                  >
                    <option value="">Select Field B</option>
                    <OrphanCombinedFieldOption field={form.aggregation?.ratioFieldB} />
                    <optgroup label="Financial">
                      <option value="CashIn">Cash In Amount</option>
                      <option value="CashOut">Cash Out Amount</option>
                      <option value="Turnover">Turnover / Wager</option>
                      <option value="NetAmount">Net Amount</option>
                    </optgroup>
                    <optgroup label="Gaming">
                      <option value="sessions.enriched.cash_in_sum">Session Cash In</option>
                      <option value="sessions.enriched.turnover_currency">Session Turnover</option>
                      <option value="TotalWon">Total Won</option>
                      <option value="TotalWagered">Total Wagered</option>
                    </optgroup>
                    <optgroup label="Gaming • Meter / combined (API keys)">
                      <option value="Turnover">Turnover</option>
                      <option value="CashIn">CashIn</option>
                      <option value="CashDrop">CashDrop</option>
                      <option value="Bill50">Bill50</option>
                      <option value="Bill100">Bill100</option>
                      <option value="Turnover_alloc">Turnover_alloc</option>
                      <option value="Bill100_alloc">Bill100_alloc</option>
                    </optgroup>
                  </select>
                </div>
                
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Operator</label>
                  <select 
                    className="select-field text-sm py-1"
                    value={form.conditions[0]?.operator || 'greater_than'}
                    onChange={e => {
                      const newConds = [...form.conditions]
                      if (!newConds[0]) newConds[0] = { field: '', operator: 'ratio', value: '', score: 20 }
                      newConds[0].operator = e.target.value
                      setForm(prev => ({...prev, conditions: newConds}))
                    }}
                  >
                    <option value="greater_than">&gt; (greater than)</option>
                    <option value="greater_than_or_equal">≥ (at least)</option>
                    <option value="less_than">&lt; (less than)</option>
                    <option value="less_than_or_equal">≤ (at most)</option>
                    <option value="equals">= (equals)</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Ratio Value</label>
                  <input 
                    type="number"
                    step="0.01"
                    className="input-field text-sm py-1"
                    placeholder="0.80"
                    value={form.conditions[0]?.value || ''}
                    onChange={e => {
                      const newConds = [...form.conditions]
                      if (!newConds[0]) newConds[0] = { field: '', operator: 'ratio', value: '', score: 20 }
                      newConds[0].value = e.target.value
                      setForm(prev => ({...prev, conditions: newConds}))
                    }}
                  />
                </div>
                
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Score</label>
                  <div className="flex items-center gap-1">
                    <input 
                      type="number"
                      min="0"
                      max="100"
                      className="input-field w-16 text-center text-sm py-1"
                      value={form.conditions[0]?.score || 20}
                      onChange={e => {
                        const newConds = [...form.conditions]
                        if (!newConds[0]) newConds[0] = { field: '', operator: 'ratio', value: '', score: 20 }
                        newConds[0].score = parseInt(e.target.value) || 0
                        setForm(prev => ({...prev, conditions: newConds}))
                      }}
                    />
                    <span className="text-xs text-gray-500">pts</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-3 p-2 bg-white border border-green-300 rounded text-xs">
                <strong className="text-green-800">Example:</strong>
                <div className="text-gray-700 mt-1">
                  {form.conditions[0]?.field && form.aggregation?.ratioFieldB && form.conditions[0]?.value ? (
                    <span>
                      Fires when <span className="font-semibold">{form.conditions[0].field} ÷ {form.aggregation.ratioFieldB}</span> is{' '}
                      <span className="font-semibold">
                        {form.conditions[0].operator === 'greater_than' && '>'} 
                        {form.conditions[0].operator === 'greater_than_or_equal' && '≥'} 
                        {form.conditions[0].operator === 'less_than' && '<'} 
                        {form.conditions[0].operator === 'less_than_or_equal' && '≤'} 
                        {form.conditions[0].operator === 'equals' && '='} 
                        {' '}{form.conditions[0].value}
                      </span>
                    </span>
                  ) : (
                    'Cash-In ÷ Turnover ≥ 0.80 (indicates low play-through / potential structuring)'
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Sequence/Absence explanation */}
          {(form.patternType === 'sequence' || form.patternType === 'absence') && (
            <div className={`mt-3 p-3 rounded border text-xs ${
              form.patternType === 'absence' 
                ? 'bg-red-50 border-red-200 text-red-800' 
                : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}>
              {form.patternType === 'sequence' 
                ? '📌 Sequence pattern: Define Event A (trigger) and Event B (must follow). The rule fires when both occur in order within the time window.'
                : '⚠️ Absence pattern: Define Event A (trigger) and Event B (must NOT follow). The rule fires when A occurs but B does NOT follow within the time window.'
              }
            </div>
          )}
        </div>

        <div className="mb-3 flex gap-3 text-sm">
          <label className="flex items-center gap-1">
            <input 
              type="radio" 
              checked={conditionMode==='simple'} 
              onChange={()=>setConditionMode('simple')} 
              disabled={form.patternType === 'ratio'}
            /> 
            Simple Conditions
          </label>
          <label className="flex items-center gap-1">
            <input 
              type="radio" 
              checked={conditionMode==='expression'} 
              onChange={()=>setConditionMode('expression')} 
              disabled={form.patternType === 'ratio'}
            /> 
            Expression Mode
          </label>
          {(form.patternType === 'sequence' || form.patternType === 'absence') && (
            <label className="flex items-center gap-1">
              <input 
                type="radio" 
                checked={conditionMode==='pattern'} 
                onChange={()=>setConditionMode('pattern')} 
              /> 
              Pattern Mode (Event A & B)
            </label>
          )}
          {form.patternType === 'ratio' && (
            <span className="text-xs text-gray-500 italic ml-2">
              (Ratio pattern uses dedicated configuration above)
            </span>
          )}
        </div>
        {conditionMode==='simple' && form.patternType !== 'ratio' ? (
          <div className="space-y-2">
            {form.conditions.map((c, idx)=> {
              const isNumericOp = ['greater_than','greater_than_or_equal','less_than','less_than_or_equal'].includes(c.operator)
              const hasOverrides = c.value_by_tier && Object.keys(c.value_by_tier).length > 0
              const tierPanelOpen = !!tierOpen[idx] || hasOverrides
              return (
              <div key={idx} className="space-y-2 p-2 border border-slate-100 rounded-md">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                <select className="select-field md:col-span-2" value={c.field} onChange={e=>updateCondition(idx,'field',e.target.value)}>
                  <option value="">Select Field</option>
                  <OrphanCombinedFieldOption field={c.field} />
                  <optgroup label="Datasets • Session-first">
                    <option value="gaming.sessions.Turnover">gaming.sessions.Turnover (per session)</option>
                    <option value="gaming.sessions.CreditWin">gaming.sessions.CreditWin (per session)</option>
                    <option value="meters.readings.CashIn">meters.readings.CashIn (interval delta)</option>
                    <option value="sessions.enriched.turnover_currency">sessions.enriched.turnover_currency</option>
                    <option value="sessions.enriched.cash_in_sum">sessions.enriched.cash_in_sum</option>
                    <option value="sessions.statsDaily.avg_turnover">sessions.statsDaily.avg_turnover (per day per member)</option>
                    <option value="sessions.statsDaily.session_count">sessions.statsDaily.session_count</option>
                    <option value="sessions.tm01.changes.avg_turnover_change_pct">tm01.changes.avg_turnover_change_pct</option>
                  </optgroup>
                  <GamingApiFieldOptgroups />
                  {/* Transaction optgroups */}
                  <optgroup label="Transaction • Financial">
                    <option value="CashIn">Cash In Amount</option>
                    <option value="CashOut">Cash Out Amount</option>
                    <option value="NetAmount">Net Amount</option>
                    <option value="TransactionAmount">Transaction Amount</option>
                    <option value="CumulativeAmount">Cumulative Amount</option>
                  </optgroup>
                  <optgroup label="Transaction • Details">
                    <option value="TransactionType">Transaction Type</option>
                    <option value="EventType">Event Type</option>
                    <option value="PaymentMethod">Payment Method</option>
                    <option value="Currency">Currency</option>
                    <option value="TransactionStatus">Transaction Status</option>
                    <option value="TransactionDate">Transaction Date</option>
                    <option value="TransactionTime">Transaction Time</option>
                  </optgroup>
                  <optgroup label="Transaction • Member">
                    <option value="MemberID">Player ID</option>
                    <option value="AccountID">Account ID</option>
                    <option value="MemberTier">Member Tier</option>
                    <option value="MemberStatus">Member Status</option>
                  </optgroup>
                  <optgroup label="Transaction • Location & Device">
                    <option value="VenueID">Venue ID</option>
                    <option value="TerminalID">Terminal ID</option>
                    <option value="DeviceType">Device Type</option>
                    <option value="IPAddress">IP Address</option>
                  </optgroup>
                  <optgroup label="Transaction • Risk & Compliance">
                    <option value="RiskScore">Risk Score</option>
                    <option value="AMLFlag">AML Flag</option>
                    <option value="SuspiciousFlag">Suspicious Flag</option>
                    <option value="ComplianceStatus">Compliance Status</option>
                    <option value="KycStatus">KYC Status</option>
                  </optgroup>
                  {/* Gaming optgroups */}
                  <optgroup label="Gaming • Session Metrics">
                    <option value="SessionDuration">Session Duration (minutes)</option>
                    <option value="TotalSpins">Total Spins</option>
                    <option value="AvgBetAmount">Average Bet Amount</option>
                    <option value="MaxBetAmount">Maximum Bet Amount</option>
                    <option value="TotalWagered">Total Wagered</option>
                    <option value="TotalWon">Total Won</option>
                    <option value="NetWinLoss">Net Win/Loss</option>
                  </optgroup>
                  <optgroup label="Gaming • Game Information">
                    <option value="GameID">Game ID</option>
                    <option value="GameType">Game Type</option>
                    <option value="GameCategory">Game Category</option>
                    <option value="GameRTP">Game RTP (%)</option>
                    <option value="GameVariance">Game Variance</option>
                  </optgroup>
                  <optgroup label="Gaming • Device & Location">
                    <option value="EGMID">EGM ID</option>
                    <option value="GMID">GM ID</option>
                    <option value="VenueID">Venue ID</option>
                    <option value="DeviceType">Device Type</option>
                    <option value="DeviceLocation">Device Location</option>
                    <option value="EgmHouseNo">EGM House No</option>
                  </optgroup>
                  <optgroup label="Gaming • Member">
                    <option value="MemberID">Player ID</option>
                    <option value="MemberTier">Member Tier</option>
                    <option value="MemberStatus">Member Status</option>
                    <option value="ResponsibleGamingStatus">Responsible Gaming Status</option>
                  </optgroup>
                  <optgroup label="Gaming • Time Analysis">
                    <option value="SessionStartTime">Session Start Time</option>
                    <option value="SessionEndTime">Session End Time</option>
                    <option value="DayOfWeek">Day of Week</option>
                    <option value="TimeOfDay">Time of Day</option>
                    <option value="IsWeekend">Is Weekend</option>
                    <option value="IsHoliday">Is Holiday</option>
                  </optgroup>
                  <optgroup label="Gaming • Behavioral Patterns">
                    <option value="DeviceChanges">Device Changes Count</option>
                    <option value="GameChanges">Game Changes Count</option>
                    <option value="SessionsToday">Sessions Today</option>
                    <option value="SessionsThisWeek">Sessions This Week</option>
                    <option value="AvgSessionDuration">Avg Session Duration (7 days)</option>
                  </optgroup>
                  <optgroup label="Gaming • Risk Indicators">
                    <option value="RapidPlayFlag">Rapid Play Flag</option>
                    <option value="ExtendedPlayFlag">Extended Play Flag</option>
                    <option value="ResponsibleGamingTrigger">RG Trigger</option>
                    <option value="AnomalyScore">Anomaly Score</option>
                  </optgroup>
                  <optgroup label="Patron Risk Profile • Overall">
                    <option value="patron_risk.overall_score">Patron Overall Risk Score (0-100)</option>
                    <option value="patron_risk.risk_level">Patron Risk Level (LOW/MEDIUM/HIGH/CRITICAL)</option>
                  </optgroup>
                  <optgroup label="Patron Risk Profile • Components">
                    <option value="patron_risk.kyc_score">KYC Compliance Score (0-100)</option>
                    <option value="patron_risk.aml_score">AML/PEP Screening Score (0-100)</option>
                    <option value="patron_risk.gaming_score">Gaming Behavior Score (0-100)</option>
                    <option value="patron_risk.occupation_score">Occupation Risk Score (0-100)</option>
                    <option value="patron_risk.case_score">Case History Score (0-100)</option>
                    <option value="patron_risk.smr_score">SMR History Score (0-100)</option>
                    <option value="patron_risk.ecdd_score">ECDD History Score (0-100)</option>
                  </optgroup>
                  <optgroup label="Patron Risk Profile • Flags">
                    <option value="patron_risk.has_pep_match">Has PEP Match</option>
                    <option value="patron_risk.has_sanctions_match">Has Sanctions Match</option>
                    <option value="patron_risk.has_active_case">Has Active Investigation</option>
                    <option value="patron_risk.tier_velocity_suspicious">Suspicious Tier Velocity</option>
                    <option value="patron_risk.occupation_high_risk">High Risk Occupation</option>
                    <option value="patron_risk.gaming_exceeds_income">Gaming Exceeds Income</option>
                  </optgroup>
                  <optgroup label="Gaming • Period Comparison">
                    <option value="periodComparison.current.sessionCount">Current Period Session Count</option>
                    <option value="periodComparison.previous.sessionCount">Previous Period Session Count</option>
                    <option value="periodComparison.current.avgTurnover">Current Period Avg Turnover</option>
                    <option value="periodComparison.previous.avgTurnover">Previous Period Avg Turnover</option>
                    <option value="periodComparison.changes.turnoverChange">Turnover Change (%)</option>
                    <option value="periodComparison.changes.winPercentageChange">Win % Change (pp)</option>
                  </optgroup>
                </select>
                <select className="select-field" value={c.operator} onChange={e=>updateCondition(idx,'operator',e.target.value)}>
                  <option value="equals">equals</option>
                  <option value="not_equals">not_equals</option>
                  <option value="greater_than">greater_than</option>
                  <option value="greater_than_or_equal">greater_than_or_equal</option>
                  <option value="less_than">less_than</option>
                  <option value="less_than_or_equal">less_than_or_equal</option>
                  <option value="contains">contains</option>
                  <option value="in">in</option>
                </select>
                <input className="input-field" placeholder="value" value={c.value} onChange={e=>updateCondition(idx,'value',e.target.value)} />
                <div className="flex items-center gap-1">
                  <input 
                    type="number" 
                    min="0" 
                    max="100" 
                    className="input-field w-16 text-center" 
                    placeholder="20" 
                    title="Risk score contribution"
                    value={c.score || 20} 
                    onChange={e=>updateCondition(idx,'score',parseInt(e.target.value) || 0)} 
                  />
                  <span className="text-xs text-gray-500">pts</span>
                </div>
                <div className="flex gap-2">
                  <button className="btn-outline" onClick={()=>removeCondition(idx)}>Remove</button>
                  {idx===form.conditions.length-1 && <button className="btn-secondary" onClick={addCondition}>Add</button>}
                </div>
              </div>
              {/* S6: per-tier thresholds — opt-in, numeric operators only */}
              {isNumericOp && (
                <div className="pt-1">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="text-xs font-semibold text-slate-600 hover:text-slate-900"
                      onClick={() => setTierOpen(prev => ({ ...prev, [idx]: !tierPanelOpen }))}
                    >
                      {tierPanelOpen ? '− Hide per-tier thresholds' : '+ Per-tier thresholds (S6)'}
                    </button>
                    {hasOverrides && !tierOpen[idx] && (
                      <span className="text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                        Tier overrides active
                      </span>
                    )}
                  </div>
                  {tierPanelOpen && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                      {['simplified','standard','enhanced','prohibited'].map(tier => (
                        <div key={tier}>
                          <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-0.5">{tier}</label>
                          <input
                            type="number"
                            step="any"
                            placeholder={c.value !== '' && c.value != null ? String(c.value) : '(base)'}
                            className="input-field text-sm py-1"
                            value={c.value_by_tier?.[tier] ?? ''}
                            onChange={e => updateConditionTier(idx, tier, e.target.value)}
                          />
                        </div>
                      ))}
                      <p className="col-span-2 md:col-span-4 text-[10px] text-slate-500 italic">
                        When a patron's CDD tier (S2) matches, the tier value is used instead of the base value. Leave blank to fall back to the base.
                      </p>
                    </div>
                  )}
                </div>
              )}
              </div>
              )
            })}
          </div>
        ) : conditionMode==='pattern' ? (
          <div className="space-y-4">
            {/* Event A */}
            <div className="border-2 border-blue-500 rounded-lg overflow-hidden">
              <div className="bg-blue-50 px-4 py-2 border-b border-blue-200">
                <h4 className="font-semibold text-blue-900 text-sm">Event A — Trigger Event</h4>
                <p className="text-xs text-blue-700">The initiating event that starts the clock</p>
              </div>
              <div className="p-3 space-y-2">
                {eventA.map((c, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-5 gap-2">
                    <select className="select-field md:col-span-2" value={c.field} onChange={e=>updateEventA(idx,'field',e.target.value)}>
                      <option value="">Select Field</option>
                      <OrphanCombinedFieldOption field={c.field} />
                      <optgroup label="Datasets • Session-first">
                        <option value="gaming.sessions.Turnover">gaming.sessions.Turnover (per session)</option>
                        <option value="gaming.sessions.CreditWin">gaming.sessions.CreditWin (per session)</option>
                        <option value="meters.readings.CashIn">meters.readings.CashIn (interval delta)</option>
                        <option value="sessions.enriched.turnover_currency">sessions.enriched.turnover_currency</option>
                        <option value="sessions.enriched.cash_in_sum">sessions.enriched.cash_in_sum</option>
                      </optgroup>
                      <GamingApiFieldOptgroups />
                      <optgroup label="Transaction • Financial">
                        <option value="CashIn">Cash In Amount</option>
                        <option value="CashOut">Cash Out Amount</option>
                        <option value="NetAmount">Net Amount</option>
                      </optgroup>
                      <optgroup label="Transaction • Details">
                        <option value="EventType">Event Type</option>
                        <option value="TransactionType">Transaction Type</option>
                      </optgroup>
                      <optgroup label="Transaction • Risk & Compliance">
                        <option value="KycStatus">KYC Status</option>
                      </optgroup>
                    </select>
                    <select className="select-field" value={c.operator} onChange={e=>updateEventA(idx,'operator',e.target.value)}>
                      <option value="equals">equals</option>
                      <option value="not_equals">not_equals</option>
                      <option value="greater_than">greater_than</option>
                      <option value="greater_than_or_equal">greater_than_or_equal</option>
                      <option value="less_than">less_than</option>
                      <option value="less_than_or_equal">less_than_or_equal</option>
                    </select>
                    <input className="input-field" placeholder="value" value={c.value} onChange={e=>updateEventA(idx,'value',e.target.value)} />
                    <div className="flex items-center gap-1">
                      <input 
                        type="number" 
                        min="0" 
                        max="100" 
                        className="input-field w-16 text-center" 
                        placeholder="20"
                        value={c.score || 20} 
                        onChange={e=>updateEventA(idx,'score',parseInt(e.target.value) || 0)} 
                      />
                      <span className="text-xs text-gray-500">pts</span>
                      <button className="btn-outline ml-2" onClick={()=>removeEventA(idx)}>×</button>
                    </div>
                  </div>
                ))}
                <button className="btn-secondary text-xs" onClick={addEventA}>+ Add Event A Condition</button>
              </div>
            </div>

            {/* Sequence Connector */}
            <div className="flex items-center justify-center py-2">
              <div className={`flex items-center gap-3 px-4 py-2 rounded-lg border-2 ${
                form.patternType === 'absence' ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300'
              }`}>
                <span className="text-2xl">{form.patternType === 'absence' ? '↛' : '→'}</span>
                <div className="text-xs">
                  <div className="font-semibold">
                    {form.patternType === 'absence' ? 'must NOT be followed by' : 'must be followed by'}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span>within</span>
                    <input
                      type="number"
                      min="1"
                      className="input-field w-16 py-0.5 text-xs"
                      value={sequenceConfig.withinMinutes}
                      onChange={e => setSequenceConfig(prev => ({...prev, withinMinutes: parseInt(e.target.value) || 30}))}
                    />
                    <span>minutes on</span>
                    <select 
                      className="select-field py-0.5 text-xs"
                      value={sequenceConfig.sameEgm}
                      onChange={e => setSequenceConfig(prev => ({...prev, sameEgm: e.target.value}))}
                    >
                      <option value="any">any EGM</option>
                      <option value="same_egm">same EGM</option>
                      <option value="same_zone">same zone</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Event B */}
            <div className={`border-2 rounded-lg overflow-hidden ${
              form.patternType === 'absence' ? 'border-red-500' : 'border-purple-500'
            }`}>
              <div className={`px-4 py-2 border-b ${
                form.patternType === 'absence' ? 'bg-red-50 border-red-200' : 'bg-purple-50 border-purple-200'
              }`}>
                <h4 className={`font-semibold text-sm ${
                  form.patternType === 'absence' ? 'text-red-900' : 'text-purple-900'
                }`}>
                  Event B — {form.patternType === 'absence' ? 'Must NOT Follow' : 'Must Follow'}
                </h4>
                <p className={`text-xs ${
                  form.patternType === 'absence' ? 'text-red-700' : 'text-purple-700'
                }`}>
                  {form.patternType === 'absence' 
                    ? 'If this event occurs within the window, the rule does NOT fire'
                    : 'This event must occur within the window for the rule to fire'
                  }
                </p>
              </div>
              <div className="p-3 space-y-2">
                {eventB.map((c, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-5 gap-2">
                    <select className="select-field md:col-span-2" value={c.field} onChange={e=>updateEventB(idx,'field',e.target.value)}>
                      <option value="">Select Field</option>
                      <OrphanCombinedFieldOption field={c.field} />
                      <optgroup label="Datasets • Session-first">
                        <option value="gaming.sessions.Turnover">gaming.sessions.Turnover (per session)</option>
                        <option value="gaming.sessions.CreditWin">gaming.sessions.CreditWin (per session)</option>
                        <option value="meters.readings.CashIn">meters.readings.CashIn (interval delta)</option>
                        <option value="sessions.enriched.turnover_currency">sessions.enriched.turnover_currency</option>
                        <option value="sessions.enriched.cash_in_sum">sessions.enriched.cash_in_sum</option>
                      </optgroup>
                      <GamingApiFieldOptgroups />
                      <optgroup label="Transaction • Financial">
                        <option value="CashIn">Cash In Amount</option>
                        <option value="CashOut">Cash Out Amount</option>
                        <option value="NetAmount">Net Amount</option>
                      </optgroup>
                      <optgroup label="Transaction • Details">
                        <option value="EventType">Event Type</option>
                        <option value="TransactionType">Transaction Type</option>
                      </optgroup>
                      <optgroup label="Transaction • Risk & Compliance">
                        <option value="KycStatus">KYC Status</option>
                      </optgroup>
                    </select>
                    <select className="select-field" value={c.operator} onChange={e=>updateEventB(idx,'operator',e.target.value)}>
                      <option value="equals">equals</option>
                      <option value="not_equals">not_equals</option>
                      <option value="greater_than">greater_than</option>
                      <option value="greater_than_or_equal">greater_than_or_equal</option>
                      <option value="less_than">less_than</option>
                      <option value="less_than_or_equal">less_than_or_equal</option>
                    </select>
                    <input className="input-field" placeholder="value" value={c.value} onChange={e=>updateEventB(idx,'value',e.target.value)} />
                    <div className="flex items-center gap-1">
                      <input 
                        type="number" 
                        min="0" 
                        max="100" 
                        className="input-field w-16 text-center" 
                        placeholder="20"
                        value={c.score || 20} 
                        onChange={e=>updateEventB(idx,'score',parseInt(e.target.value) || 0)} 
                      />
                      <span className="text-xs text-gray-500">pts</span>
                      <button className="btn-outline ml-2" onClick={()=>removeEventB(idx)}>×</button>
                    </div>
                  </div>
                ))}
                <button className="btn-secondary text-xs" onClick={addEventB}>+ Add Event B Condition</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <label className="block text-xs text-gray-600">Expressions (each row is AND-ed)</label>
            {expressions.map((ex, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <input className="input-field font-mono flex-1" placeholder={`Expression #${idx+1}`} value={ex} onChange={e=>{
                  const v = e.target.value; setExpressions(prev=>prev.map((x,i)=> i===idx? v : x))
                }} />
                <button className="btn-outline" onClick={()=> setExpressions(prev=> prev.filter((_,i)=>i!==idx))}>Remove</button>
              </div>
            ))}
            <div>
              <button className="btn-secondary" onClick={()=> setExpressions(prev=> [...prev, ''])}>Add Expression</button>
            </div>
            <p className="text-xs text-gray-500">Tip: Use field names like ${'{'}CashDrop{'}'}, ${'{'}Turnover{'}'} etc.</p>
          </div>
        )}
      </div>

      {/* Weighted Scoring Configuration */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Weighted Scoring</h3>
          <button 
            type="button"
            className="text-xs text-blue-600 hover:underline"
            onClick={() => setShowScoringConfig(!showScoringConfig)}
          >
            {showScoringConfig ? 'Hide Scoring' : 'Configure Scoring'}
          </button>
        </div>
        
        {showScoringConfig && (
          <div className="space-y-3 border-t pt-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="text-sm font-medium text-blue-900 mb-2">Score Summary</div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-gray-600">Total Possible Score</div>
                  <div className="text-2xl font-bold text-blue-600">{calculateTotalScore()}</div>
                </div>
                <div>
                  <div className="text-gray-600">Trigger Threshold</div>
                  <div className="text-2xl font-bold text-orange-600">{form.scoreThreshold}</div>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-600">
                {calculateTotalScore() > 0 && (
                  <span>
                    Threshold is {Math.round((form.scoreThreshold / calculateTotalScore()) * 100)}% of max score.
                    {Math.round((form.scoreThreshold / calculateTotalScore()) * 100) < 40 && ' ⚠️ Very sensitive — high false-positive risk.'}
                    {Math.round((form.scoreThreshold / calculateTotalScore()) * 100) > 90 && ' ⚠️ Very restrictive — rule may rarely fire.'}
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-2">Score Trigger Threshold</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="0"
                  max={Math.max(calculateTotalScore(), 100)}
                  value={form.scoreThreshold}
                  onChange={e => setForm(prev => ({...prev, scoreThreshold: parseInt(e.target.value)}))}
                  className="flex-1"
                />
                <input
                  type="number"
                  min="0"
                  max={Math.max(calculateTotalScore(), 100)}
                  value={form.scoreThreshold}
                  onChange={e => setForm(prev => ({...prev, scoreThreshold: parseInt(e.target.value) || 0}))}
                  className="input-field w-20 text-center"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Rule fires when total score meets or exceeds this threshold</p>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Alert Narrative Template (optional)</label>
              <textarea
                className="input-field font-mono text-xs"
                rows={2}
                placeholder="Patron {{patron.name}} triggered {{rule.code}} with score {{score}}."
                value={form.narrativeTemplate}
                onChange={e => setForm(prev => ({...prev, narrativeTemplate: e.target.value}))}
              />
              <p className="text-xs text-gray-500 mt-1">
                Available variables: {'{'}{'{'} patron.name {'}'}{'}'}, {'{'}{'{'} rule.code {'}'}{'}'}, {'{'}{'{'} score {'}'}{'}'}, {'{'}{'{'} threshold {'}'}{'}'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Risk Level Selection */}
      <div className="card">
        <h3 className="font-semibold mb-2">Risk Assessment</h3>
        <p className="text-xs text-gray-500 mb-3">
          How serious is this pattern? This determines the default response actions.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          
          {/* LOW RISK */}
          <label className={`
            p-3 border-2 rounded-lg cursor-pointer transition
            ${riskLevel === 'low' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'}
          `}>
            <input 
              type="radio" 
              name="riskLevel" 
              value="low" 
              checked={riskLevel === 'low'}
              onChange={() => handleRiskLevelChange('low')}
              className="sr-only"
            />
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <div className="font-semibold">Low Risk</div>
            </div>
            <div className="text-xs text-gray-600">
              Informational patterns that need basic monitoring
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Example: Slight increase in activity, new EGM preference
            </div>
          </label>

          {/* MEDIUM RISK */}
          <label className={`
            p-3 border-2 rounded-lg cursor-pointer transition
            ${riskLevel === 'medium' ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 hover:border-yellow-300'}
          `}>
            <input 
              type="radio" 
              name="riskLevel" 
              value="medium" 
              checked={riskLevel === 'medium'}
              onChange={() => handleRiskLevelChange('medium')}
              className="sr-only"
            />
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="font-semibold">Medium Risk</div>
            </div>
            <div className="text-xs text-gray-600">
              Notable patterns requiring enhanced monitoring
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Example: High-value transactions, frequent visits
            </div>
          </label>

          {/* HIGH RISK */}
          <label className={`
            p-3 border-2 rounded-lg cursor-pointer transition
            ${riskLevel === 'high' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'}
          `}>
            <input 
              type="radio" 
              name="riskLevel" 
              value="high" 
              checked={riskLevel === 'high'}
              onChange={() => handleRiskLevelChange('high')}
              className="sr-only"
            />
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <div className="font-semibold">High Risk</div>
            </div>
            <div className="text-xs text-gray-600">
              Suspicious patterns requiring investigation
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Example: Structuring indicators, unusual patterns
            </div>
          </label>

          {/* CRITICAL RISK */}
          <label className={`
            p-3 border-2 rounded-lg cursor-pointer transition
            ${riskLevel === 'critical' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-red-300'}
          `}>
            <input 
              type="radio" 
              name="riskLevel" 
              value="critical" 
              checked={riskLevel === 'critical'}
              onChange={() => handleRiskLevelChange('critical')}
              className="sr-only"
            />
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="font-semibold">Critical Risk</div>
            </div>
            <div className="text-xs text-gray-600">
              Severe violations requiring immediate action
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Example: Sanctions matches, extreme amounts
            </div>
          </label>

        </div>
      </div>

      {/* Execution Mode Selection */}
      <div className="card">
        <h3 className="font-semibold mb-2">Action Execution Mode</h3>
        <p className="text-xs text-gray-500 mb-3">
          How should the system respond when this rule triggers?
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          
          {/* AUTOMATIC */}
          <label className={`
            p-3 border-2 rounded-lg cursor-pointer transition
            ${executionMode === 'automatic' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}
          `}>
            <input 
              type="radio" 
              name="executionMode" 
              value="automatic" 
              checked={executionMode === 'automatic'}
              onChange={(e) => setExecutionMode(e.target.value)}
              className="sr-only"
            />
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <div className="font-semibold">Automatic</div>
            </div>
            <div className="text-xs text-gray-600 mb-2">
              Execute all configured actions immediately when rule fires
            </div>
            <div className="text-xs text-green-600 font-medium">
              ✓ Instant response (milliseconds)
            </div>
            <div className="text-xs text-green-600">
              ✓ Consistent handling
            </div>
            <div className="text-xs text-amber-600 mt-1">
              ⚠️ Review results after execution
            </div>
          </label>

          {/* RECOMMEND */}
          <label className={`
            p-3 border-2 rounded-lg cursor-pointer transition
            ${executionMode === 'recommend' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'}
          `}>
            <input 
              type="radio" 
              name="executionMode" 
              value="recommend" 
              checked={executionMode === 'recommend'}
              onChange={(e) => setExecutionMode(e.target.value)}
              className="sr-only"
            />
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              <div className="font-semibold">Recommend</div>
              <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Default</span>
            </div>
            <div className="text-xs text-gray-600 mb-2">
              Suggest actions for officer approval (one-click execution)
            </div>
            <div className="text-xs text-green-600 font-medium">
              ✓ Human oversight before action
            </div>
            <div className="text-xs text-green-600">
              ✓ One-click to execute all
            </div>
            <div className="text-xs text-blue-600 mt-1">
              💡 Best for starting out
            </div>
          </label>

          {/* MANUAL */}
          <label className={`
            p-3 border-2 rounded-lg cursor-pointer transition
            ${executionMode === 'manual' ? 'border-gray-500 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}
          `}>
            <input 
              type="radio" 
              name="executionMode" 
              value="manual" 
              checked={executionMode === 'manual'}
              onChange={(e) => setExecutionMode(e.target.value)}
              className="sr-only"
            />
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-gray-500"></div>
              <div className="font-semibold">Manual</div>
            </div>
            <div className="text-xs text-gray-600 mb-2">
              Create alert only, officer decides all actions manually
            </div>
            <div className="text-xs text-green-600 font-medium">
              ✓ Maximum control
            </div>
            <div className="text-xs text-green-600">
              ✓ Context-based decisions
            </div>
            <div className="text-xs text-gray-600 mt-1">
              ⏱️ Slower response time
            </div>
          </label>

        </div>

        {/* Execution Mode Explanation */}
        <div className="mt-3 p-3 bg-gray-50 rounded text-xs">
          {executionMode === 'automatic' && (
            <div>
              <strong className="text-blue-700">Automatic Mode:</strong> When this rule triggers, all configured actions below will execute immediately. 
              The alert, watchlist entry, case, and notifications will be created in under 1 second. 
              Compliance officers can review and undo if needed.
            </div>
          )}
          {executionMode === 'recommend' && (
            <div>
              <strong className="text-purple-700">Recommend Mode:</strong> When this rule triggers, an alert will be created with recommended actions attached. 
              The compliance officer can review the alert and click "Execute Recommended Actions" to apply all at once, or customize before executing.
            </div>
          )}
          {executionMode === 'manual' && (
            <div>
              <strong className="text-gray-700">Manual Mode:</strong> When this rule triggers, only an alert will be created. 
              The compliance officer must manually decide whether to add to watchlist, create a case, or take other actions. 
              The configured actions below serve as guidelines only.
            </div>
          )}
        </div>
      </div>

      {/* Automated Actions */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Automated Response Actions</h3>
          <button 
            type="button"
            className="text-xs text-blue-600 hover:underline"
            onClick={() => setShowActionCustomization(!showActionCustomization)}
          >
            {showActionCustomization ? 'Hide Customization' : 'Customize Actions'}
          </button>
        </div>

        {/* Show recommended actions */}
        <div className={`
          p-3 rounded-lg mb-3
          ${riskLevel === 'low' && 'bg-green-50 border border-green-200'}
          ${riskLevel === 'medium' && 'bg-yellow-50 border border-yellow-200'}
          ${riskLevel === 'high' && 'bg-orange-50 border border-orange-200'}
          ${riskLevel === 'critical' && 'bg-red-50 border border-red-200'}
        `}>
          <div className="text-sm font-medium mb-2">
            Default actions for {riskLevel.toUpperCase()} RISK patterns:
          </div>
          <div className="text-sm space-y-1">
            {getDefaultActions(riskLevel).map((action, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="text-green-600">✓</span>
                <span>{action}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Expandable customization */}
        {showActionCustomization && (
          <div className="space-y-3 border-t pt-3">
            
            <label className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded">
              <input 
                type="checkbox" 
                checked={actions.createAlert}
                onChange={(e) => setActions({...actions, createAlert: e.target.checked})}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-sm">Create Alert</div>
                <div className="text-xs text-gray-600">Log this event for compliance review</div>
                {actions.createAlert && (
                  <select 
                    className="select-field mt-1 text-xs w-full"
                    value={actions.alertSeverity}
                    onChange={(e) => setActions({...actions, alertSeverity: e.target.value})}
                  >
                    <option value="low">Low Severity</option>
                    <option value="medium">Medium Severity</option>
                    <option value="high">High Severity</option>
                    <option value="critical">Critical Severity</option>
                  </select>
                )}
              </div>
            </label>

            <label className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded">
              <input 
                type="checkbox" 
                checked={actions.addToWatchlist}
                onChange={(e) => setActions({...actions, addToWatchlist: e.target.checked})}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-sm">Add to Watchlist</div>
                <div className="text-xs text-gray-600">Enhanced monitoring for this member</div>
              </div>
            </label>

            <label className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded">
              <input 
                type="checkbox" 
                checked={actions.createCase}
                onChange={(e) => {
                  const checked = e.target.checked
                  setActions({
                    ...actions, 
                    createCase: checked,
                    // Auto-disable ECDD/SMR if case creation is disabled
                    startECDD: checked ? actions.startECDD : false,
                    startSMR: checked ? actions.startSMR : false
                  })
                }}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-sm">Create Investigation Case</div>
                <div className="text-xs text-gray-600">Open formal investigation with evidence tracking</div>
              </div>
            </label>

            {/* ECDD/SMR Workflows - Only available if Create Case is enabled */}
            {actions.createCase && (
              <div className="ml-8 pl-4 border-l-2 border-blue-200 space-y-2">
                <div className="text-xs font-medium text-blue-700 mb-2">
                  Case Workflows (available when case is created)
                </div>
                
                <label className="flex items-start gap-3 p-2 hover:bg-blue-50 rounded">
                  <input 
                    type="checkbox" 
                    checked={actions.startECDD}
                    onChange={(e) => setActions({...actions, startECDD: e.target.checked})}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm">Auto-Start ECDD Workflow</div>
                    <div className="text-xs text-gray-600">
                      Automatically initiate Enhanced Customer Due Diligence process when case is created
                    </div>
                    <div className="text-xs text-blue-600 mt-1">
                      Recommended for: High-value transactions, PEP matches, complex patterns
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-2 hover:bg-blue-50 rounded">
                  <input 
                    type="checkbox" 
                    checked={actions.startSMR}
                    onChange={(e) => setActions({...actions, startSMR: e.target.checked})}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm">Auto-Start SMR Preparation</div>
                    <div className="text-xs text-gray-600">
                      Automatically begin Suspicious Matter Report preparation (3-day filing window)
                    </div>
                    <div className="text-xs text-amber-600 mt-1">
                      ⚠️ Use carefully: Only for patterns that strongly indicate ML/TF activity
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-2 hover:bg-blue-50 rounded">
                  <input 
                    type="checkbox" 
                    checked={actions.recalculatePatronRisk}
                    onChange={(e) => setActions({...actions, recalculatePatronRisk: e.target.checked})}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm">Recalculate Patron Risk Score</div>
                    <div className="text-xs text-gray-600">
                      Trigger immediate patron risk profile recalculation (updates case history component)
                    </div>
                    <div className="text-xs text-blue-600 mt-1">
                      ℹ️ Recommended: New case affects risk scoring
                    </div>
                  </div>
                </label>
              </div>
            )}

            <label className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded">
              <input 
                type="checkbox" 
                checked={actions.requireReview}
                onChange={(e) => setActions({...actions, requireReview: e.target.checked})}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-sm">Require Manager Review</div>
                <div className="text-xs text-gray-600">Transaction pending until reviewed by compliance officer</div>
              </div>
            </label>

            <div className="border-t pt-3 mt-3">
              <div className="text-xs font-medium text-gray-700 mb-2">Notifications</div>
              
              <label className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded">
                <input 
                  type="checkbox" 
                  checked={actions.notifyEmail}
                  onChange={(e) => setActions({...actions, notifyEmail: e.target.checked})}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">Email Notification</div>
                  <div className="text-xs text-gray-600">Send email to compliance team</div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded">
                <input 
                  type="checkbox" 
                  checked={actions.notifySMS}
                  onChange={(e) => setActions({...actions, notifySMS: e.target.checked})}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">SMS Notification</div>
                  <div className="text-xs text-gray-600">Send SMS to compliance manager (urgent alerts)</div>
                </div>
              </label>
            </div>

          </div>
        )}
      </div>

      {/* Alert Policy Configuration */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Alert Policy</h3>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={alertPolicyEnabled}
              onChange={(e) => setAlertPolicyEnabled(e.target.checked)}
            />
            Configure alert policy
          </label>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Control when alerts are created. Without a policy, alerts are created on every trigger (default).
        </p>

        {alertPolicyEnabled && (
          <div className="space-y-4">
            {/* Mode selection */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">Alert Mode</label>
              <select
                className="select-field"
                value={alertPolicy.mode}
                onChange={(e) => setAlertPolicy(prev => ({ ...prev, mode: e.target.value }))}
              >
                <option value="on_first_trigger">On First Trigger (default)</option>
                <option value="on_nth_trigger">On Nth Trigger</option>
                <option value="on_first_then_suppress">On First, Then Suppress</option>
                <option value="every_trigger">Every Trigger (no dedupe)</option>
                <option value="when_no_open_case">When No Open Case</option>
              </select>
            </div>

            {/* Mode-specific fields */}
            {alertPolicy.mode === 'on_nth_trigger' && (
              <div className="p-3 bg-blue-50 rounded space-y-3">
                <div className="text-xs text-blue-700 font-medium">Alert only when trigger count reaches N</div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">N (trigger threshold)</label>
                  <input
                    type="number" min="1" max="10"
                    className="input-field w-32"
                    value={alertPolicy.n}
                    onChange={(e) => setAlertPolicy(prev => ({ ...prev, n: parseInt(e.target.value) || 1 }))}
                  />
                  <div className="text-xs text-gray-500 mt-1">Triggers 1 to N-1 are recorded but no alert is surfaced. Max: 10.</div>
                </div>
              </div>
            )}

            {alertPolicy.mode === 'on_first_then_suppress' && (
              <div className="p-3 bg-amber-50 rounded space-y-3">
                <div className="text-xs text-amber-700 font-medium">Alert on first trigger, then suppress for a period</div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Suppress for (hours)</label>
                  <input
                    type="number" min="1"
                    className="input-field w-32"
                    value={alertPolicy.suppress_hours}
                    onChange={(e) => setAlertPolicy(prev => ({ ...prev, suppress_hours: parseInt(e.target.value) || 168 }))}
                  />
                  <div className="text-xs text-gray-500 mt-1">{Math.round((alertPolicy.suppress_hours || 168) / 24)} days</div>
                </div>
                <div className="border-t border-amber-200 pt-3">
                  <div className="text-xs font-medium text-amber-800 mb-2">Escalation Override (recommended for high/critical severity)</div>
                  <div className="text-xs text-gray-600 mb-2">If aggregate activity during suppression crosses this threshold, suppression is lifted and a new alert fires.</div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Field</label>
                      <select
                        className="select-field text-xs"
                        value={alertPolicy.escalation_override?.field || 'CashIn'}
                        onChange={(e) => setAlertPolicy(prev => ({
                          ...prev, escalation_override: { ...prev.escalation_override, field: e.target.value }
                        }))}
                      >
                        <option value="CashIn">CashIn</option>
                        <option value="Turnover">Turnover</option>
                        <option value="CashOut">CashOut</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Window (hours)</label>
                      <input
                        type="number" min="1"
                        className="input-field text-xs"
                        value={alertPolicy.escalation_override?.aggregate_window_hours || 24}
                        onChange={(e) => setAlertPolicy(prev => ({
                          ...prev, escalation_override: { ...prev.escalation_override, aggregate_window_hours: parseInt(e.target.value) || 24 }
                        }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Threshold ($)</label>
                      <input
                        type="number" min="0"
                        className="input-field text-xs"
                        value={alertPolicy.escalation_override?.threshold || 5000}
                        onChange={(e) => setAlertPolicy(prev => ({
                          ...prev, escalation_override: { ...prev.escalation_override, threshold: parseInt(e.target.value) || 0 }
                        }))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Dedupe key mode */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">Deduplication Key</label>
              <select
                className="select-field"
                value={alertPolicy.dedupe_key_mode}
                onChange={(e) => setAlertPolicy(prev => ({ ...prev, dedupe_key_mode: e.target.value }))}
              >
                <option value="member">Member (recommended)</option>
                <option value="session">Session</option>
                <option value="transaction">Transaction</option>
              </select>
              <div className="text-xs text-gray-500 mt-1">
                Member-level groups all triggers for the same person. Session/Transaction are more granular.
              </div>
            </div>

            {/* Combined condition */}
            {alertPolicy.mode === 'on_nth_trigger' && (
              <div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={alertPolicy.and?.includes('when_no_open_case')}
                    onChange={(e) => {
                      setAlertPolicy(prev => ({
                        ...prev,
                        and: e.target.checked ? ['when_no_open_case'] : []
                      }))
                    }}
                  />
                  Also require: no open case exists (when_no_open_case)
                </label>
                <div className="text-xs text-gray-500 mt-1">
                  When enabled, alerts are suppressed if the member already has an open investigation case for this rule.
                </div>
              </div>
            )}

            {/* Validation results */}
            {policyValidation.errors.length > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded">
                <div className="text-xs font-medium text-red-800 mb-1">Validation Errors</div>
                {policyValidation.errors.map((err, i) => (
                  <div key={i} className="text-xs text-red-700">{err}</div>
                ))}
              </div>
            )}
            {policyValidation.warnings.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                <div className="text-xs font-medium text-amber-800 mb-1">Compliance Warnings</div>
                {policyValidation.warnings.map((warn, i) => (
                  <div key={i} className="text-xs text-amber-700 mb-1">{warn}</div>
                ))}
                <label className="flex items-center gap-2 text-xs mt-2 text-amber-900 font-medium">
                  <input
                    type="checkbox"
                    checked={acknowledgeWarnings}
                    onChange={(e) => setAcknowledgeWarnings(e.target.checked)}
                  />
                  I acknowledge the compliance risk and wish to proceed
                </label>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card">
        <h3 className="font-semibold mb-2">SMR / Regulatory Tags (optional)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-gray-600 mb-1">Offence Types</div>
            <div className="space-y-1">
              {offenceOptions.map(o=> (
                <label key={o.code} className="flex items-center gap-2">
                  <input type="checkbox" checked={tags.offence_codes.includes(o.code)} onChange={(e)=> setTags(prev=> ({ ...prev, offence_codes: e.target.checked ? [...prev.offence_codes, o.code] : prev.offence_codes.filter(c=>c!==o.code) }))} />
                  {o.code} — {o.desc}
                </label>
              ))}
            </div>
          </div>
          <div>
            <div className="text-gray-600 mb-1">Designated Services</div>
            <div className="space-y-1">
              {serviceOptions.map(o=> (
                <label key={o.code} className="flex items-center gap-2">
                  <input type="checkbox" checked={tags.service_codes.includes(o.code)} onChange={(e)=> setTags(prev=> ({ ...prev, service_codes: e.target.checked ? [...prev.service_codes, o.code] : prev.service_codes.filter(c=>c!==o.code) }))} />
                  {o.code} — {o.desc}
                </label>
              ))}
            </div>
          </div>
          <div>
            <div className="text-gray-600 mb-1">Suspicion Reasons</div>
            <div className="space-y-1">
              {reasonOptions.map(o=> (
                <label key={o.code} className="flex items-center gap-2">
                  <input type="checkbox" checked={tags.reason_codes.includes(o.code)} onChange={(e)=> setTags(prev=> ({ ...prev, reason_codes: e.target.checked ? [...prev.reason_codes, o.code] : prev.reason_codes.filter(c=>c!==o.code) }))} />
                  {o.code} — {o.desc}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button className="btn-outline" onClick={saveRule} disabled={saving}>Save Rule</button>
        <button className="btn-primary" onClick={runTest}>Test Rule</button>
      </div>

      <div className="card">
        <h3 className="font-semibold mb-2">Test Data (JSON)</h3>
        <textarea className="input-field font-mono" rows={8} value={testPayload} onChange={e=>setTestPayload(e.target.value)} />
        {testResult && (
          <pre className="mt-2 text-xs bg-gray-50 border rounded p-2 overflow-auto max-h-64">{JSON.stringify(testResult, null, 2)}</pre>
        )}
      </div>
    </div>
  )
}


